/* eslint-disable no-mixed-spaces-and-tabs */
import type { SingleForm as Props } from './types';
import type Field from '@feathery/react/dist/utils/entities/Field.d.ts';
import type { FieldValues, TrackingParams } from '@/types';
import { useState, useEffect, useRef } from 'react';
import { Form } from '@feathery/react';
import type { FormContext } from '@feathery/react';
import * as Sentry from '@sentry/nextjs';
import RichText from '@/components/RichText';
import Loader from '@/components/Loader';
import { convertToJson, setHiddenFields, checkFeatheryElements } from '@/utils/forms';
import { FormMessages } from '@/constants';
import style from './SingleForm.module.scss';
import { getTrackingParams, pushVWO } from '@/utils/analytics';
import { usePostHog } from 'posthog-js/react';
import { FeatheryError, SubmitError } from '@/types/errors';
import { generateURL } from '@/utils/getDomain';
import { useRouter } from 'next/router';
export default function SingleForm({
  showFinePrint,
  finePrint,
  showLogos,
  form,
  backgroundColor
}: Props) {
  const [message, setMessage] = useState<string>(FormMessages.DEFAULT);
  const [loading, setLoading] = useState<boolean>(true);
  const [redirecting, setRedirecting] = useState<boolean>(false);
  const [extraFormData, setExtraFromData] = useState<FieldValues>({});
  const [currentStep, setCurrentStep] = useState<string>('');
  const [stepList, setStepList] = useState<string[]>([]);
  const [trackingParams, setTrackingParams] = useState<TrackingParams>({
    fbclid: '',
    gclid: '',
    wbraid: '',
    gbraid: '',
    rdt_cid: ''
  });
  const context = useRef<FormContext>(null);
  const formActivated = useRef(false);
  const initialized = useRef(false);
  const posthog = usePostHog();
  const startTime = useRef(performance.now()); // Will only set once when component first mounts

  const initTimeout = useRef<NodeJS.Timeout>();
  const FORM_INIT_TIMEOUT = 10000; // 10 seconds

  const router = useRouter();
  const beforeUnloadEventHandler = (event: BeforeUnloadEvent) => {
    event.stopImmediatePropagation();
  };
  const getOtherFields = () => {
    const arEl = document.querySelector('#anura_response_0');
    const arElValue = arEl?.getAttribute('value');
    const fsEl = document.querySelector('#full_story_id_0');
    const fsElValue = fsEl?.getAttribute('value');
    const url = generateURL();
    const newDataObject: FieldValues = {
      ...extraFormData,
      pageurl: url,
      anura_response: arElValue || '',
      fsid: fsElValue || '',
      posthog_session_id: posthog.get_session_id(),
      posthog_distinct_id: posthog.get_distinct_id()
    };
    return newDataObject;
  };
  const onPageLoad = async (params: TrackingParams) => {
    /** this event listener exists to prevent Third Party Scripts from preventing
     * navigation away from page with the "leave site?" dialog
     */
    window.addEventListener('beforeunload', beforeUnloadEventHandler);
    const url = generateURL();
    const urlParams = new URLSearchParams(window.location.search);
    const hiddenFields = await setHiddenFields(urlParams, url, params);
    const certUrlElement = document.querySelector('[name="xxTrustedFormCertUrl"]');
    posthog.capture('trusted_form_status', {
      tf_loaded: !!certUrlElement,
      tf_certUrl: certUrlElement?.getAttribute('value')
    });
    setExtraFromData(hiddenFields);
  };
  const getCommonEventProps = (ctx: FormContext) => {
    const {
      stepName,
      previousStepName,
      totalSteps
    } = ctx.getStepProperties();
    return {
      form_id: form?.value?.data.formId,
      form_name: form?.value?.data.name,
      form_type: form?.['@type'],
      step_name: stepName,
      prev_step_name: previousStepName,
      total_steps: totalSteps,
      on_last_step: ctx.isLastStep(),
      number_of_form_steps: totalSteps
    };
  };
  useEffect(() => {
    // Prevent multiple calls to onPageLoad in React Strict Mode
    if (initialized.current) return;
    initialized.current = true;
    const params = getTrackingParams();
    setTrackingParams(params);
    pushVWO();
    onPageLoad(params);
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadEventHandler);
    };
  }, []);
  const redirectOnSubmit = async (fieldValues: Record<string, Field>) => {
    try {
      setRedirecting(true);
      setMessage(FormMessages.CHECKING);
      window.location.href = '#form';
      const _extra = await getOtherFields();
      const baseEventProps = getCommonEventProps(context.current!);
      if (fieldValues.xxTrustedFormCertUrl?.value === undefined) {
        Sentry.withScope(scope => {
          scope.setTag('analytics', 'adblocker');
          scope.setTag('page', window.location.pathname);
          scope.setTag('window.trustedForm', JSON.stringify(window.trustedForm));
          scope.setTag('document.querySelector(\'script[src*="trustedform.com"]\')', JSON.stringify(document.querySelector('script[src*="trustedform.com"]')));
          scope.setLevel('warning');
          Sentry.captureMessage('Trusted form certificate not found');
        });
      }
      const fieldValuesSimple = convertToJson(fieldValues);

      // Add analytics tags like anura, trusted form, etc.
      const reqBody = JSON.stringify({
        ...fieldValuesSimple,
        ..._extra,
        form_metadata: {
          ...baseEventProps,
          step_number: stepList.indexOf(currentStep) + 1,
          time_on_form: performance.now() - startTime.current
        }
      });
      const response = await fetch('/api/submit', {
        method: 'POST',
        body: reqBody,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const responseData = await response.json();
      if (response.status !== 200) {
        throw new Error(responseData.error || 'Unknown server error');
      }
      if (responseData.success === false) {
        throw new Error(responseData.error?.details || 'Unknown server error');
      }
      if (responseData.result) {
        let testURLString = '';
        if (process.env.VERCEL_ENV !== 'production') {
          const params = new URLSearchParams(window.location.search);
          testURLString = params.get('test_url') ? `&test_url=${params.get('test_url')}` : '';
        }
        sessionStorage.setItem('trackingParams', JSON.stringify(trackingParams));
        setMessage(FormMessages.REDIRECTING);
        const queryParams = {
          sold: responseData.sold,
          pixel: responseData.pixel,
          pageurl: responseData.pageurl,
          pixel_id: responseData.pixel_id,
          connection_id: responseData.connection_id,
          contract_id: responseData.contract_id,
          lead_id: responseData.lead_id,
          ...(testURLString ? {
            test_url: new URLSearchParams(window.location.search).get('test_url')
          } : {})
        };
        await router.push({
          pathname: '/thank-you',
          query: queryParams
        });
      }
    } catch (error) {
      setRedirecting(false);
      Sentry.withScope(scope => {
        scope.setTag('api', 'submit API');
        scope.setLevel('error');
        Sentry.captureException(new SubmitError(error as Error));
      });
      setMessage(FormMessages.SERVER_ERROR);
      onPageLoad(trackingParams);
    }
  };
  const handleInitialLoad = (stepName: string, baseEventProps: object) => {
    const loadTime = performance.now() - startTime.current;
    setCurrentStep(stepName);
    setStepList(prev => [...prev, stepName]);
    setLoading(false);
    posthog.capture('form_loaded', {
      ...baseEventProps,
      load_time_ms: loadTime,
      step_number: 1
    });
  };
  const handleStepChange = (stepName: string, baseEventProps: object) => {
    const isNewStep = !stepList.includes(stepName);
    const stepNumber = isNewStep ? stepList.length + 1 : stepList.indexOf(stepName) + 1;
    if (isNewStep) {
      setStepList(prev => [...prev, stepName]);
    }
    posthog.capture('form_step_changed', {
      ...baseEventProps,
      step_number: stepNumber
    });
    setCurrentStep(stepName);
  };
  const handleFormLoad = (ctx: FormContext) => {
    const {
      stepName
    } = ctx.getStepProperties();
    const baseEventProps = getCommonEventProps(ctx);
    if (loading) {
      handleInitialLoad(stepName, baseEventProps);
      return;
    }
    checkFeatheryElements();
    if (stepName !== currentStep) {
      handleStepChange(stepName, baseEventProps);
    }
  };
  useEffect(() => {
    // Set timeout for form initialization
    initTimeout.current = setTimeout(() => {
      if (loading) {
        Sentry.withScope(scope => {
          scope.setTag('api', 'feathery');
          scope.setTag('error_type', 'timeout');
          scope.setLevel('error');
          Sentry.captureException(new FeatheryError(`Feathery form initialization took over ${FORM_INIT_TIMEOUT}ms`));
        });
        setMessage(FormMessages.FORM_LOAD_ERROR);
        setLoading(false);
      }
    }, FORM_INIT_TIMEOUT);
    return () => {
      clearTimeout(initTimeout.current);
    };
  }, [loading]);
  useEffect(() => {
    // const button = getButton();
    // if (button !== null)button.disabled = true;
    // setTimeout(() => {
    // 	releaseButton(false);
    // }, 1000);
  }, [extraFormData]);
  return <div className={style.formWrapper} id="form" style={{
    backgroundColor: backgroundColor || 'white'
  }} data-sentry-component="SingleForm" data-sentry-source-file="SingleForm.tsx">
			{!redirecting && <Form data-testid="feathery-form" className={style.form} contextRef={context} formId={form?.value?.data.formId || ''} onFormComplete={async _context => {
      await redirectOnSubmit(_context.fields);
    }} onChange={() => {}}
    // onChange={()=>{ releaseButton(true); }}
    onLoad={handleFormLoad} onAction={async ctx => {
      if (!formActivated.current) {
        formActivated.current = true;
        const baseEventProps = {
          ...getCommonEventProps(ctx),
          step_number: stepList.indexOf(ctx.getStepProperties().stepName) === -1 ? 1 : stepList.indexOf(ctx.getStepProperties().stepName) + 1
        };
        posthog.capture('form_activated', {
          ...baseEventProps,
          hasGA: !!window.gaGlobal,
          hasGTM: !!window.google_tag_manager,
          hasAnura: !!window.Anura,
          hasVWO: !!window.VWO || !!window._vwo_code,
          hasFBPixel: !!window.fbq,
          hasTrustedForm: !!window.trustedForm || document.querySelector('script[src*="trustedform.com"]') !== null
        });
      }
    }} />}
			{message !== '' && <div className={style.message}>{message}</div>}
			{(loading || redirecting) && <Loader />}
			<div className={style.hiddenFields}></div>

			<aside className={showFinePrint ? style.finePrint : style.hideDisplay}>
				<div className={showLogos ? style.icons : style.hideDisplay}>
					<img src="/secureIcons.webp" alt={`Submitting this form will get a confidential review.
							This is a secure site.`} />
				</div>
				{finePrint && <RichText text={finePrint} />}
			</aside>
			<form id="hiddenFields"></form>
		</div>;
}

// export default withChildren(SingleForm);